import { capitalize } from "@/utils/string";
import { ref, toRaw } from "vue";

function formatAuthor(author, idx, citationType, total) {
  const lastname = capitalize(author.lastname);
  const name = capitalize(author.name);
  switch (citationType) {
    case "apa":
    case "harvard":
      if (idx === total - 2) return `${lastname}, ${name} and `;
      if (idx !== total - 1) return `${lastname}, ${name}, `;
      return `${lastname}, ${name}`;
    case "vancouver":
      return `${lastname} ${name.charAt(0)}.${idx !== total - 1 ? ", " : ""}`;
    case "chicago":
      return `${name} ${lastname}${idx !== total - 1 ? ", " : ""}`;
    case "mla":
      return idx === 0
        ? total > 1
          ? `${lastname}, ${name}, et al`
          : `${lastname}, ${name}`
        : "";
    case "ieee":
      return `${name.charAt(0)}. ${lastname}${idx !== total - 1 ? ", " : ""}`;
    default:
      return `${lastname}, ${name}`;
  }
}

// Returns null when the date is missing or not a valid dd/mm/yyyy string
function parseDMY(dateStr) {
  if (!dateStr) return null;
  const [day, month, year] = dateStr.split("/");
  // 2-digit years are 20xx, as in InvestigationStore
  const fullYear = year?.length === 2 ? `20${year}` : year;
  const d = new Date(Number(fullYear), Number(month) - 1, Number(day));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function buildCitation(opts) {
  const d = parseDMY(opts.date);
  let formattedDate = "";
  let day = "";
  let year = "";
  if (d) {
    const dayName = d
      .toLocaleDateString("en-US", { weekday: "long" })
      .slice(0, 3);
    const month = d.toLocaleDateString("en-US", { month: "long" });
    day = d.getDate();
    year = d.getFullYear();
    formattedDate = `${dayName} ${month} ${day}, ${year}`;
  }
  // Date fragments are left out entirely when there is no date
  const datePar = formattedDate ? ` (${formattedDate})` : "";
  const yearComma = year ? `, ${year}` : "";
  const yearSemi = year ? `; ${year}` : "";

  const citationAuthors = opts.authors
    .map((a, i) => formatAuthor(a, i, opts.citationType, opts.authors.length))
    .join("");

  switch (opts.citationType) {
    case "mla":
      return `${citationAuthors}. ${opts.title} . Elettra Sincrotrone Trieste${yearComma}, ${opts.doiUrl}.`;
    case "apa":
      return `${citationAuthors}${datePar}. ${opts.title} [Data set]. Elettra Sincrotrone Trieste. ${opts.doiUrl}`;
    case "chicago":
      return `${citationAuthors}. "${opts.title}". Elettra Sincrotrone Trieste${yearComma}. ${opts.doiUrl}.`;
    case "harvard":
      return `${citationAuthors}${datePar}. "${opts.title}". Elettra Sincrotrone Trieste. DOI:${opts.doi}.`;
    case "vancouver":
      return `${citationAuthors}. ${opts.title} [Internet]. Elettra Sincrotrone Trieste${yearSemi}. Available from: ${opts.vuoUrl}.`;
    case "ieee":
      return `${citationAuthors}. "${opts.title}". Elettra Sincrotrone Trieste${yearComma}. ${opts.doiUrl}.`;
    case "bibtex": {
      const note = d
        ? `\n  note = {${d.toLocaleString("en-US", { month: "short" })}. ${day}, ${year}},`
        : "";
      return `@misc{${opts.title},
  journal = {dataset},${note}
  organization = {Elettra Sincrotrone Trieste},
  title = {${opts.title}},
  DOI = {${opts.doi}},
  url = {${opts.doiUrl}}
}`;
    }
  }
}

export function useCitationComposer() {
  const citation = ref("");
  const doiUrl = ref("");
  const doiUrlEncoded = ref("");
  const vuoUrl = ref("");

  // Expected input shape:
  // {
  //   title: string,                    // dataset title (without "Dataset of")
  //   upload_type: "Test" | string,     // upload type
  //   uploaders: Array,                 // authors array (used by buildCitation)
  //   doi?: string | null,              // DOI returned by backend (if any)
  //   investigation_id: string | number,// VUO investigation id
  //   environment?: "prod" | "dev",     // optional, defaults to "prod"
  //   citationType?: "apa" | string     // optional, defaults to "apa"
  // }

  function compose(input) {
    const {
      title: rawTitle,
      date,
      upload_type,
      uploaders,
      doi: doiData,
      investigation_id,
      citationType = "apa",
    } = input;

    const title = `Dataset of ${rawTitle}`;
    console.log("compose input", input);

    // env-specific base fragments (mirrors original logic)
    const environment =
      localStorage.getItem("development") === "false" ? "prod" : "dev";
    const isProd = environment === "prod";
    const host = "https://vuo.elettra.eu";
    const vlabPath = isProd ? "/pls/vuo" : "/pls/root";
    const openAccessPath = isProd ? "/pls/vuo" : "/pls/root";

    let _doi = doiData;
    let _doiUrl = "";
    let _doiUrlEncoded = "";
    let _vuoUrl = "";

    if (upload_type === "Test") {
      const testMsg = "This is a test upload: No DOI has been created";
      _doi = testMsg;
      _doiUrl = testMsg;
      _doiUrlEncoded = testMsg;
      _vuoUrl = `${host}${vlabPath}/vlab.show_view_investigation?FRM_ID=${investigation_id}`;
    } else if (!_doi) {
      const testMsg = "No DOI has been created";
      _doi = testMsg;
      _doiUrl = testMsg;
      _doiUrlEncoded = testMsg;
      _vuoUrl = `${host}${vlabPath}/vlab.show_view_investigation?FRM_ID=${investigation_id}`;
    } else {
      _vuoUrl = `${host}${openAccessPath}/open_access_data_portal.show_view_investigation?FRM_ID=${investigation_id}`;
      _doiUrl = `https://doi.org/${_doi}`;
      _doiUrlEncoded = `https://doi.org/${encodeURIComponent(_doi)}`;
    }

    doiUrl.value = _doiUrl;
    doiUrlEncoded.value = _doiUrlEncoded;
    vuoUrl.value = _vuoUrl;

    citation.value = buildCitation({
      title,
      date,
      authors: toRaw(uploaders),
      doi: _doi,
      doiUrl: _doiUrl,
      vuoUrl: _vuoUrl,
      citationType,
    });

    console.log("citation useCitation", citation);
  }

  return { citation, doiUrl, doiUrlEncoded, vuoUrl, compose };
}
